// Gemeinsame Bausteine der Finanz-Verträge. Geld in Cent (Integer), Sätze in Basispunkten, Tage als 'YYYY-MM-DD'
// (Europe/Berlin). Vorzeichen wie im Zahlungskern: Rechnung +, Rechnungskorrektur −; Zahlungseingang +, Auszahlung −.
import { zId } from '@shared/ids'
import { z } from 'zod'

export const INVOICE_TYPE = 'invoice'
export const CREDIT_NOTE_TYPE = 'credit_note'
/** Belegarten des Verkaufs, aus denen die Finanzen ableiten */
export const DELIVERY_TYPE = 'delivery_note'
export const RETURN_TYPE = 'sales_return'
/** Belegart des Einkaufs, deren offene Posten die Finanzen zahlen */
export const SUPPLIER_INVOICE_TYPE = 'supplier_invoice'

/** Kalendertag 'YYYY-MM-DD' (Europe/Berlin). */
export const zDay = z.iso.date({ error: 'Bitte ein gültiges Datum eingeben.' })
/** Monat 'YYYY-MM'. */
export const zMonth = z.string({ error: 'Bitte einen Monat wählen.' }).regex(/^\d{4}-(0[1-9]|1[0-2])$/, { error: 'Bitte einen Monat in der Form JJJJ-MM angeben.' })

/** Betrag in Cent, größer als null. */
export const zAmountCents = z
  .int({ error: 'Bitte einen Betrag eingeben.' })
  .min(1, { error: 'Der Betrag muss größer als 0 sein.' })
  .max(1_000_000_000, { error: 'Der Betrag ist zu groß.' })

export const zAmountOrZeroCents = z
  .int({ error: 'Bitte einen Betrag eingeben.' })
  .min(0, { error: 'Der Betrag darf nicht negativ sein.' })
  .max(1_000_000_000, { error: 'Der Betrag ist zu groß.' })

export const zReason = z
  .string({ error: 'Bitte geben Sie einen Grund an.' })
  .trim()
  .min(3, { error: 'Bitte geben Sie einen Grund an (mindestens 3 Zeichen).' })
  .max(500, { error: 'Der Grund ist zu lang (höchstens 500 Zeichen).' })

export const zNote = z.string().trim().max(500, { error: 'Die Notiz ist zu lang (höchstens 500 Zeichen).' }).nullish()

export const zPartnerRef = z.object({ id: zId, number: z.string().nullable(), name: z.string() })
export type PartnerRef = z.output<typeof zPartnerRef>
export const zLocationRef = z.object({ id: zId, code: z.string(), name: z.string() })
export const zDocumentRef = z.object({ id: zId, type: z.string(), typeTitle: z.string(), number: z.string().nullable() })

/** Seite eines Partnerkontos: Forderungen (Kunde) oder Verbindlichkeiten (Lieferant). */
export const SIDES = ['receivable', 'payable'] as const
export type Side = (typeof SIDES)[number]

/** Zahlarten, die die Buchhaltung bucht (Gutscheine und Pfandbons gehören der Kasse). */
export const PAYMENT_METHODS = ['transfer', 'cash', 'ec_card', 'credit_card', 'direct_debit', 'online', 'other'] as const
export type FinancePaymentMethod = (typeof PAYMENT_METHODS)[number]
export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  transfer: 'Überweisung',
  cash: 'Bar',
  ec_card: 'Girocard',
  credit_card: 'Kreditkarte',
  direct_debit: 'Lastschrift',
  online: 'Online-Zahlung',
  voucher: 'Gutschein',
  deposit_voucher: 'Pfandbon',
  other: 'Sonstige',
}

/** Altersstruktur eines offenen Postens zum Stichtag. */
export const AGING_BUCKETS = ['notDue', 'days1to30', 'days31to60', 'days61to90', 'over90'] as const
export type AgingBucket = (typeof AGING_BUCKETS)[number]
export const AGING_LABELS: Record<AgingBucket, string> = {
  notDue: 'nicht fällig',
  days1to30: '1–30 Tage',
  days31to60: '31–60 Tage',
  days61to90: '61–90 Tage',
  over90: 'über 90 Tage',
}
export const zAging = z.object({ notDue: z.int(), days1to30: z.int(), days31to60: z.int(), days61to90: z.int(), over90: z.int() })

export const agingBucketOf = (daysOverdue: number): AgingBucket => (daysOverdue <= 0 ? 'notDue' : daysOverdue <= 30 ? 'days1to30' : daysOverdue <= 60 ? 'days31to60' : daysOverdue <= 90 ? 'days61to90' : 'over90')

// ---- E-Rechnung (Modul `einvoice`, SPEZIFIKATION §4.1, §4.15) ----------------------------------------------------
// Die Finanzen zeigen am Beleg und im Rechnungslauf, ob eine XRechnung entsteht bzw. entstanden ist. Die Formen hier
// spiegeln die Verträge `einvoice.outbound.preflight|preflightDraft|get` (Verträge importieren nur @shared und zod).

/** Hinweis, wenn der Kunde E-Rechnungen erwartet, die Funktion E-Rechnung im Betrieb aber ausgeschaltet ist. */
export const EINVOICE_DISABLED_NOTICE =
  'Für diesen Kunden ist E-Rechnung als Rechnungsformat hinterlegt. Die Funktion E-Rechnung ist für Ihren Betrieb ausgeschaltet – es entsteht keine XRechnung, der Beleg entsteht als Druck bzw. PDF. Einschalten lässt sie sich unter Einstellungen › Funktionen.'

/** Befund der Vorprüfung (P-01 … P-20, Regel-IDs der Norm) – ein fertiger Satz mit Feld (BT/BG) und Korrekturweg. */
export const zEInvoiceFinding = z.object({
  code: z.string(),
  level: z.enum(['error', 'warning', 'info']),
  message: z.string(),
  /** BT/BG oder Feldpfad, z. B. 'BT-49' */
  field: z.string().nullable(),
  /** Route zum Datensatz, an dem sich der Befund beheben lässt */
  fix: z.string().nullable(),
})
export type EInvoiceFindingView = z.output<typeof zEInvoiceFinding>

/** Vorprüfung einer geplanten oder entworfenen Rechnung: Entsteht eine XML – und fehlt dafür etwas? */
export const zEInvoiceCheck = z.object({
  /** beim Festschreiben entsteht eine XRechnung */
  required: z.boolean(),
  /** mandatory | requested | public | not_required | disabled | not_applicable | suppressed */
  reason: z.string(),
  reasonText: z.string(),
  /** Festschreiben scheitert: XML nötig und mindestens ein Befund „error“ */
  blocking: z.boolean(),
  syntax: z.enum(['ubl', 'cii']).nullable(),
  typeCode: z.enum(['380', '381', '384']).nullable(),
  findings: z.array(zEInvoiceFinding),
})
export type EInvoiceCheckView = z.output<typeof zEInvoiceCheck>

/**
 * Stand der E-Rechnung eines Belegs:
 *   issued     XML abgelegt (Download über `einvoice.outbound.download { outboundId }`)
 *   expected   Entwurf – beim Festschreiben entsteht eine XML
 *   blocked    Entwurf – eine XML müsste entstehen, es fehlen Angaben (Festschreiben scheitert)
 *   missing    festgeschrieben ohne XML, obwohl eine nötig wäre (etwa vor dem Einschalten) – nachträglich ausstellbar
 *   none       keine XML nötig (Grund im Text)
 *   suppressed Storno im Ablauf „Rechnung berichtigen“ – bekommt bewusst keine XML
 */
export const EINVOICE_STATES = ['issued', 'expected', 'blocked', 'missing', 'none', 'suppressed'] as const
export type EInvoiceState = (typeof EINVOICE_STATES)[number]
export const EINVOICE_STATE_LABELS: Record<EInvoiceState, string> = {
  issued: 'E-Rechnung ausgestellt',
  expected: 'E-Rechnung entsteht beim Festschreiben',
  blocked: 'Für die E-Rechnung fehlen Angaben',
  missing: 'E-Rechnung fehlt',
  none: 'Keine E-Rechnung',
  suppressed: 'Storno ohne E-Rechnung',
}

export const zInvoiceEInvoice = z.object({
  state: z.enum(EINVOICE_STATES),
  stateLabel: z.string(),
  /** ein Satz für die Seitenkarte am Beleg */
  text: z.string(),
  /** mandatory | requested | public | not_required | disabled | not_applicable | suppressed */
  reason: z.string().nullable(),
  /** die abgelegte XML (nur `issued`) */
  outbound: z
    .object({
      id: zId,
      fileName: z.string(),
      typeCode: z.string(),
      typeLabel: z.string(),
      syntax: z.enum(['ubl', 'cii']),
      syntaxLabel: z.string(),
      issueDate: z.string(),
      sha256: z.string(),
      sizeBytes: z.int(),
      reasonLabel: z.string(),
      originLabel: z.string(),
      createdAt: z.date(),
      /** Inhalt liegt vor (false nach Ablauf der Aufbewahrungsfrist) – nur dann lässt sie sich herunterladen */
      available: z.boolean(),
      /** Art für Menschen („Storno zu RE-…“, „Berichtigte Rechnung zu RE-…“) */
      kindLabel: z.string().optional(),
      /** Der Beleg gilt nicht mehr – storniert bzw. durch eine berichtigte Rechnung ersetzt; die Datei gilt dann nicht mehr */
      superseded: z
        .object({ kind: z.enum(['reversed', 'replaced']), documentId: zId, number: z.string().nullable(), ownEInvoice: z.boolean() })
        .nullable()
        .optional(),
    })
    .nullable(),
  /** Downloads und Übermittlungen, älteste zuerst */
  deliveries: z.array(
    z.object({
      channel: z.string(),
      channelLabel: z.string(),
      at: z.date(),
      byName: z.string().nullable(),
      /** an wen (per E-Mail: die angenommenen Adressen) */
      recipient: z.string().nullable().optional(),
      /** done | failed | queued – gescheiterte E-Mails zählen nicht als übermittelt */
      status: z.enum(['queued', 'done', 'failed']).optional(),
      note: z.string().nullable().optional(),
    }),
  ),
  findings: z.array(zEInvoiceFinding),
  /** `einvoice.outbound.issue` wäre möglich (festgeschrieben, ohne XML, Funktion an, Recht vorhanden) */
  canIssue: z.boolean(),
})
export type InvoiceEInvoiceView = z.output<typeof zInvoiceEInvoice>

/** Pflichthinweis am Export (recht §10, §15): kein Partnerstatus, kein Testimport behauptet. */
export const DATEV_NOTICE = 'Format nach DATEV-Schnittstellenbeschreibung – vor dem ersten Echtlauf mit der Steuerkanzlei abstimmen'

/** Hinweis an Zahlungsvorschlag und Zahlungsliste. */
export const NO_BANK_NOTICE = 'Keine Verbindung zur Bank: Die Zahlungsliste ist eine Datei für Ihr Online-Banking. Gebucht wird mit „Als bezahlt buchen“ – oder im Bankabgleich, sobald der Kontoauszug die Überweisung zeigt.'

/** Zustände eines Lieferscheins (Modul Verkauf), in denen er noch zu berechnen ist */
export const BILLABLE_DELIVERY_STATUSES = ['delivered', 'partially_invoiced'] as const

/** Kleinbetragsrechnung (§ 33 UStDV): bis zu diesem Bruttobetrag genügen die vereinfachten Angaben */
export const SMALL_INVOICE_LIMIT_CENTS = 25_000
